import { RootState } from '../Store';
import { createSelector } from '@reduxjs/toolkit';

// Base selector
export const selectDashboard = (state: RootState) => state.dashboard;

// Stats selectors
export const selectDashboardStats = (state: RootState) => state.dashboard.stats;
export const selectPersonalStats = (state: RootState) => state.dashboard.stats?.personal;
export const selectSocialStats = (state: RootState) => state.dashboard.stats?.social;
export const selectAreaStats = (state: RootState) => state.dashboard.stats?.area;
export const selectRecentActivity = (state: RootState) => state.dashboard.stats?.recentActivity || [];

export const selectTopProfiles = (state: RootState) => state.dashboard.topProfiles;
export const selectTrendingProfiles = (state: RootState) => state.dashboard.trendingProfiles;
export const selectActiveSpotlightedProfiles = (state: RootState) => state.dashboard.activeSpotlightedProfiles;

// Feed selectors
export const selectActiveFeedTab = (state: RootState) => state.dashboard.activeFeedTab;
export const selectSpotlightFeed = (state: RootState) => state.dashboard.spotlightFeed;
export const selectLocationFeed = (state: RootState) => state.dashboard.locationFeed;
export const selectKlyqueFeed = (state: RootState) => state.dashboard.klyqueFeed;
export const selectFeedHasMore = (state: RootState) => state.dashboard.feedHasMore;
export const selectFeedOffset = (state: RootState) => state.dashboard.feedOffset;

// Active feed based on tab
export const selectActiveFeed = createSelector(
  [selectActiveFeedTab, selectSpotlightFeed, selectLocationFeed, selectKlyqueFeed],
  (activeTab, spotlight, location, klyque) => {
    switch (activeTab) {
      case 'spotlight':
        return spotlight;
      case 'location':
        return location;
      case 'klyque':
        return klyque;
      default:
        return location;
    }
  }
);

// Favorites selectors
export const selectMyFavorites = (state: RootState) => state.dashboard.myFavorites;
export const selectFavoriteStats = (state: RootState) => state.dashboard.favoriteStats;

// Follow selectors
export const selectKlyque = (state: RootState) => state.dashboard.klyque;
export const selectKlicks = (state: RootState) => state.dashboard.klicks;
export const selectFollowStats = (state: RootState) => state.dashboard.followStats;
export const selectSuggestedFollows = (state: RootState) => state.dashboard.suggestedFollows;

// Loading selectors
export const selectIsLoading = (state: RootState) => state.dashboard.isLoading;
export const selectStatsLoading = (state: RootState) => state.dashboard.statsLoading;
export const selectFeedLoading = (state: RootState) => state.dashboard.feedLoading;
export const selectFollowLoading = (state: RootState) => state.dashboard.followLoading;
export const selectError = (state: RootState) => state.dashboard.error;

// Computed selectors
export const selectKlyqueCount = (state: RootState) => state.dashboard.followStats?.klyqueCount || 0;
export const selectKlicksCount = (state: RootState) => state.dashboard.followStats?.klicksCount || 0;
export const selectMutualFollowsCount = (state: RootState) => state.dashboard.followStats?.mutualCount || 0;

export const selectKlyxdToday = (state: RootState) => state.dashboard.favoriteStats?.received.today || 0;
export const selectKlyxdThisWeek = (state: RootState) => state.dashboard.favoriteStats?.received.thisWeek || 0;
export const selectKlyxdAllTime = (state: RootState) => state.dashboard.favoriteStats?.received.allTime || 0;

// Check if user is following someone
export const selectIsFollowing = (userId: string) =>
  createSelector([selectKlyque], (klyque) =>
    klyque.some((user) => user.userId === userId)
  );

// Check if profile is favorited
export const selectIsProfileFavorited = (profileId: string) =>
  createSelector([selectMyFavorites], (favorites) =>
    favorites.some((profile) => profile.id === profileId)
  );
